/**
 * @name Responsibilities
 * @description Componente de responsabilidades del usuario en configuración de perfil
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';
import { trackPromise } from 'react-promise-tracker';
import { CSVLink } from 'react-csv';

import { Environment } from '@config';
import { DataGrid, LoadingWrapper } from '@components';
import { Avatar } from '@styles';

import {
  AuthService,
  EmployeeService,
  NotificationsService,
  ResponseHandlerService,
  ResponsibilitiesService,
  SnackbarService,
  ValidateDataService
} from '@services';

import { Icons, MUI, Typography } from '@styles';

import AuthInfo from './components/auth-info';
import CommentDialog from './components/comment.dialog';
import ResponsibilitiesDialog from './components/responsibilities.dialog';

class Responsibilities extends Component {
  constructor(props) {
    super(props);
    this.dataGridRef = React.createRef();
    const infoUsuario = JSON.parse(localStorage.getItem('ls.infoUsuario') || '{}');
    this.enterpriseParams = infoUsuario && infoUsuario.Empresa ? infoUsuario.Empresa : null;
    this.userInfo = infoUsuario && infoUsuario.Empleado ? infoUsuario.Empleado : null;
    if (!this.enterpriseParams || !this.userInfo) {
      AuthService.redirectToLogin();
      return;
    }
    this.state = {
      employeeId: props.queryParams.get('idEmpleado') ? props.queryParams.get('idEmpleado') : this.userInfo.IdEmpleado,
      isThirdLevel: this.userInfo.NivelJerarquia === 3,
      isSupervisor: this.userInfo.NivelJerarquia >= 2,
      minItems: this.enterpriseParams.CantidadResponsabilidadMin,
      maxItems: this.enterpriseParams.CantidadResponsabilidadMax,
      employeeFilter: {
        id: this.userInfo.IdEmpleado,
        name: `${this.userInfo.Nombres} ${this.userInfo.Apellidos}`,
        status: '',
        photo: '',
        job: ''
      },
      permission: {
        user: (this.userInfo.IdEmpleado === this.userInfo.IdReporta) ? true : this.enterpriseParams.AgregaResponsabilidadUsuario,
        supervisor: this.enterpriseParams.AgregaResponsabilidadSupervisor,
        thirdLevel: this.enterpriseParams.AgregaResponsabilidadTercerNivel
      },
      gridConfig: {
        rows: [],
        columns: [
          { id: 'Id', label: i18n.t('Id'), visible: false, filter: null, },
          { id: 'Nombre', label: i18n.t('Responsibilities.responsibility'), visible: true, filter: null, sort: true },
          { id: 'Peso', label: i18n.t('GenericMessages.weight'), visible: true, filter: null, sort: true }
        ],
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canSelect: false,
      },
      status: {
        id: '',
        status: '',
        comment: ''
      },
      structure: [],
      selectedEmployeeFromFilter: null,
      totalSum: 0,
      totalItems: 0,
      ownItems: false,
      showCommentDialog: {
        action: '',
        open: false
      },
      showResponsibilitiesDialog: {
        action: '',
        data: {},
        open: false
      }
    };
  }

  componentDidMount() {
    const {
      employeeId,
      isSupervisor,
      isThirdLevel
    } = this.state;

    const {
      queryParams,
      onChangeTab
    } = this.props;

    // Entra desde un correo
    if (queryParams.get('idEmpleado')) {
      EmployeeService.getOne(queryParams.get('idEmpleado'))
        .then((response) => {
          const selectedEmployeeFromFilter = {
            IdEmpleado: response.IdEmpleado,
            IdReporta: response.IdReporta,
            Nombre: response.Nombre,
            Foto: response.Foto,
            Puesto: response.Puesto
          };

          this.setState({ selectedEmployeeFromFilter }, () => {
            this.filterResponsibilities(selectedEmployeeFromFilter);
          });
        })
        .catch(() => {
          ResponseHandlerService.error();
        });
    }

    // Entra desde una notificación
    if (localStorage.getItem('ls.Parametros')) {
      let params = JSON.parse(localStorage.getItem('ls.Parametros'));
      localStorage.removeItem('ls.Parametros');
      onChangeTab(params.Tab ? params.Tab : 0);
      if (params.filter) {
        EmployeeService.getOne(params.filter.Id)
          .then((response) => {
            const selectedEmployeeFromFilter = {
              IdEmpleado: response.IdEmpleado,
              IdReporta: response.IdReporta,
              Nombre: response.Nombre,
              Foto: response.Foto,
              Puesto: response.Puesto
            };

            this.setState({ selectedEmployeeFromFilter }, () => {
              this.filterResponsibilities(selectedEmployeeFromFilter);
            });
          })
          .catch(() => {
            ResponseHandlerService.error();
          });
      }
    }

    this.checkStatus(employeeId);
    this.getResponsibilities(employeeId);
    if (isSupervisor || isThirdLevel) {
      this.getStructure();
    }
  }

  /**
   * Consulta las responsabilidades de un usuario
   * @param {number} employeeId
   */
  getResponsibilities = (employeeId) => {
    if (employeeId) {
      trackPromise(
        ResponsibilitiesService.get({ page: 0, size: 0, filter: { id: employeeId }, columns: null })
          .then((response) => {
            const { gridConfig } = this.state;
            gridConfig.rows = response;
            let weights = response.map ? response.map((item) => item.Peso) : '';
            this.setState((prevState) => ({
              gridConfig: { ...prevState.gridConfig, gridConfig },
              totalItems: weights.length,
              totalSum: weights.reduce ? weights.reduce((prev, current) => prev + current) : 0
            }));
          })
          .catch((error) => {
            ResponseHandlerService.error(error);
            const { gridConfig } = this.state;
            gridConfig.rows = [];
            this.setState((prevState) => ({
              gridConfig: { ...prevState.gridConfig, gridConfig },
              totalItems: 0,
              totalSum: 0
            }));
          }),
        'loading-tab-responsibilities'
      );
    }
  }

  /**
   * Consulta la estructura del supervisor o tercer nivel
   */
  getStructure = () => {
    const configFilter = {
      tipoEstructura: true,
      estructuraDirecta: true
    };

    EmployeeService.getStructure({ page: 0, size: 0, filter: configFilter })
      .then((response) => {
        this.setState({ structure: response });
      })
      .catch(() => { });
  }

  /**
   * Filtra las responsabilidades de un usuario
   * @param {object} employee
   */
  filterResponsibilities = (employee) => {
    const employeeFilter = {
      id: employee ? employee.IdEmpleado : this.userInfo.IdEmpleado,
      name: employee ? employee.Nombre : `${this.userInfo.Nombres} ${this.userInfo.Apellidos}`,
      photo: employee ? employee.Foto : '',
      job: employee ? employee.Puesto : ''
    };
    const employeeId = employee ? employee.IdEmpleado : this.userInfo.IdEmpleado;

    this.setState({
      employeeFilter: {
        ...employeeFilter
      },
      isSupervisor: employee ? employee.IdReporta === this.userInfo.IdEmpleado : this.userInfo.NivelJerarquia >= 2,
      isThirdLevel: employee ? employee.IdReporta !== this.userInfo.IdEmpleado : this.userInfo.NivelJerarquia === 3,
    }, () => {
      this.getResponsibilities(employeeId);
      this.checkStatus(employeeId);
    });
  }

  checkStatus = (employeeId) => {
    ResponsibilitiesService.checkStatus(employeeId)
      .then((response) => {
        const status = { id: response.Id, status: response.Estatus, comment: response.Comentario };
        this.validateEmployee(status);
      });
  }

  validateEmployee = (status) => {
    const {
      employeeFilter,
      gridConfig,
      isSupervisor,
      isThirdLevel,
      permission
    } = this.state;

    let ownItems;

    if (this.userInfo.IdEmpleado === employeeFilter.id) {
      gridConfig.canAdd = status.id === 1 || status.id === 4 ? permission.user : false;
      gridConfig.canEdit = status.id === 1 || status.id === 4 ? permission.user : false;
      gridConfig.canDelete = status.id === 1 || status.id === 4 ? permission.user : false;
      gridConfig.canSelect = status.id === 1 || status.id === 4 ? permission.user : false;
      ownItems = true;
    } else {
      if (isSupervisor) {
        gridConfig.canAdd = status.id === 1 || status.id === 4 ? permission.supervisor : false;
        gridConfig.canEdit = status.id === 1 || status.id === 4 ? permission.supervisor : false;
        gridConfig.canDelete = status.id === 1 || status.id === 4 ? permission.supervisor : false;
        gridConfig.canSelect = status.id === 1 || status.id === 4 ? permission.supervisor : false;
        ownItems = false;
      } else {
        if (isThirdLevel) {
          gridConfig.canAdd = status.id === 1 || status.id === 4 ? permission.thirdLevel : false;
          gridConfig.canEdit = status.id === 1 || status.id === 4 ? permission.thirdLevel : false;
          gridConfig.canDelete = status.id === 1 || status.id === 4 ? permission.thirdLevel : false;
          gridConfig.canSelect = status.id === 1 || status.id === 4 ? permission.thirdLevel : false;
          ownItems = false;
        }
      }
    }

    this.setState((prevState) => ({
      gridConfig: { ...prevState.gridConfig, gridConfig },
      ownItems,
      status
    }));
  }

  sendToAuthorize = () => {
    const { gridConfig, minItems, maxItems } = this.state;
    if (ValidateDataService.isCorrectList(gridConfig.rows, minItems, maxItems, 100, 'Peso')) {
      this.changeStatus(2);
    } else {
      let message = `${i18n.t('Responsibilities.minRespIs')}${minItems}${i18n.t('Responsibilities.maxRespIs')}${maxItems}${i18n.t('Responsibilities.sumRespWeight')}`;
      SnackbarService.error(message);
    }
  }

  returnToActiveStatus = () => {
    this.setState({ showCommentDialog: { action: 'return', open: true } });
  }

  returnStatusConfirmation = ({ comment }, resetForm) => {
    this.changeStatus(1, comment);
    this.closeDialog('comment');
    resetForm();
  }

  approve = () => {
    const { gridConfig, minItems, maxItems } = this.state;
    if (ValidateDataService.isCorrectList(gridConfig.rows, minItems, maxItems, 100, 'Peso')) {
      this.changeStatus(3);
    } else {
      let message = `${i18n.t('Responsibilities.minRespIs')}${minItems}${i18n.t('Responsibilities.maxRespIs')}${maxItems}${i18n.t('Responsibilities.sumRespWeight')}`;
      SnackbarService.error(message);
    }
  }

  reject = () => {
    this.setState({ showCommentDialog: { action: 'reject', open: true } });
  }

  rejectConfirmation = ({ comment }, resetForm) => {
    this.changeStatus(4, comment);
    this.closeDialog('comment');
    resetForm();
  }

  changeStatus = (statusId, comment) => {
    const { employeeFilter } = this.state;
    let commentStatus, rejectComment;

    if (statusId === 1) {
      commentStatus = comment;
    } else {
      if (statusId === 4) {
        rejectComment = comment;
      }
    }

    let request = {
      Id: statusId,
      IdEmpleado: employeeFilter.id,
      Comentario: (statusId === 1) ? commentStatus : rejectComment
    };

    let notificationToSend = {
      IdAmbiente: Environment.getCurrent().environmentId,
      URL: '#/configuraperfil',
    };

    switch (statusId) {
      case 1: // Activo
        // Notificación - Tu supervisor cambió el estatus de tus responsabilidades a activo
        notificationToSend.IdTipoNotificacion = 4;
        notificationToSend.IdEmpleado = employeeFilter.id;
        notificationToSend.Parametros = JSON.stringify({ Tab: 2 });
        break;

      case 2: // Por aprobar
        // Notificación - Tienes responsabilidades pendientes por aprobar de <colaborador>
        notificationToSend.IdTipoNotificacion = 7;
        notificationToSend.IdEmpleado = this.userInfo.IdReporta;
        notificationToSend.Parametros = JSON.stringify({ filter: { Id: employeeFilter.id, IdReporta: this.userInfo.IdReporta, Nombre: employeeFilter.name }, Tab: 2 });
        notificationToSend.Propiedades = JSON.stringify({ colaborador: employeeFilter.name });
        break;

      case 3: // Autorizado
        // Notificación - Tu supervisor ha aprobado tus responsabilidades
        notificationToSend.IdTipoNotificacion = 5;
        notificationToSend.IdEmpleado = employeeFilter.id;
        notificationToSend.Parametros = JSON.stringify({ Tab: 2 });
        break;

      case 4: // Rechazado
        // Notificación - Tu supervisor ha rechazado tus responsabilidades
        notificationToSend.IdTipoNotificacion = 6;
        notificationToSend.IdEmpleado = employeeFilter.id;
        notificationToSend.Parametros = JSON.stringify({ Tab: 2 });
        break;

      default:
        break;
    }

    ResponsibilitiesService.changeStatus(request)
      .then((response) => {
        this.checkStatus(employeeFilter.id);
        if (this.userInfo.IdEmpleado !== notificationToSend.IdEmpleado) {
          NotificationsService.post(notificationToSend);
        }
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }

  closeDialog = (dialog) => {
    if (dialog === 'comment') {
      this.setState({ showCommentDialog: { action: '', open: false } });
    } else if (dialog === 'responsibilities') {
      this.setState({ showResponsibilitiesDialog: { action: '', data: {}, open: false } });
    }
  }

  showResponsibilitiesDialog = (item, action) => {
    const { totalSum } = this.state;
    let data = {};
    data.MaxWeight = 100 - totalSum;

    if (item) {
      data = { ...item[0] };
      data.MaxWeight = (100 - totalSum) + item[0].Peso;
    }

    this.setState({ showResponsibilitiesDialog: { action, data, open: true } });
  };

  addResponsibility = (values, resetForm, setSubmitting) => {
    const { employeeFilter } = this.state;
    let request = {
      Nombre: values.name,
      Peso: values.weight,
      IdEmpleado: employeeFilter.id
    };

    ResponsibilitiesService.post(request)
      .then((response) => {
        setSubmitting(false);
        resetForm();
        this.closeDialog('responsibilities');
        this.getResponsibilities(employeeFilter.id);
        this.dataGridRef.current?.resetState();
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        setSubmitting(false);
        ResponseHandlerService.error(error && error.response ? error : null);
      });
  }

  editResponsibility = (values, resetForm, setSubmitting) => {
    const { employeeFilter } = this.state;
    let request = {
      Id: values.id,
      Nombre: values.name,
      Peso: values.weight,
      IdEmpleado: employeeFilter.id
    };

    ResponsibilitiesService.put(request)
      .then((response) => {
        setSubmitting(false);
        resetForm();
        this.closeDialog('responsibilities');
        this.getResponsibilities(employeeFilter.id);
        this.dataGridRef.current?.resetState();
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        setSubmitting(false);
        ResponseHandlerService.error(error && error.response ? error : null);
      });
  }

  deleteResponsibility = (item) => {
    const { employeeFilter } = this.state;
    let id = item[0].Id;
    let request = {
      Id: id,
      EmployeeId: employeeFilter.id
    };

    ResponsibilitiesService.delete(request)
      .then((response) => {
        this.getResponsibilities(employeeFilter.id);
        this.dataGridRef.current.resetState();
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }

  render() {
    const {
      employeeFilter,
      selectedEmployeeFromFilter,
      gridConfig,
      isSupervisor,
      isThirdLevel,
      permission,
      structure,
      minItems,
      maxItems,
      totalItems,
      totalSum,
      ownItems,
      status,
    } = this.state;

    return (
      <>
        {/* Buscar colabordador (Supervisor/Tercer Nivel) */}
        {(isSupervisor || isThirdLevel) && (
          <>
            <Styled.SearchWrapper>
              {/* Info Colaborador */}
              <Styled.BoxSeeingOf>
                <Styled.BoxIcon>
                  <Icons.Info />
                </Styled.BoxIcon>
                {employeeFilter.id === this.userInfo.IdEmpleado ? (
                  <Typography.H6>{`${i18n.t('Responsibilities.seeingMy')}.`}</Typography.H6>
                ) : (
                  <Typography.H6>{`${i18n.t('Responsibilities.seeingOf')}:`}</Typography.H6>
                )}
              </Styled.BoxSeeingOf>
              <MUI.Grid container alignItems="center">
                {/* Colaborador */}
                <MUI.Grid item md={3} sm={6} xs={12}>
                  <Styled.FlexSearch>
                    {selectedEmployeeFromFilter && (
                      <Styled.Avatar shadow size="sm" src={selectedEmployeeFromFilter.Foto ? selectedEmployeeFromFilter.Foto : 'https://assets.talentry.io/icons/profile/TAL_Avatar.png'} />
                    )}
                    <Styled.FlexAutocomplete>
                      <MUI.Autocomplete
                        label={i18n.t('GenericMessages.employee')}
                        searchList={structure}
                        searchBy="Nombre"
                        value={selectedEmployeeFromFilter}
                        onChange={(newValue) => {
                          this.filterResponsibilities(newValue);
                          this.setState({ selectedEmployeeFromFilter: newValue });
                        }}
                      />
                    </Styled.FlexAutocomplete>
                  </Styled.FlexSearch>
                </MUI.Grid>
              </MUI.Grid>
            </Styled.SearchWrapper>
          </>
        )}

        <LoadingWrapper area="loading-tab-responsibilities">
          <Styled.ContentWrapper>
            <MUI.Box padding="8px">
              <DataGrid
                ref={this.dataGridRef}
                columns={gridConfig.columns}
                rows={gridConfig.rows}
                canAdd={gridConfig.canAdd}
                canEdit={gridConfig.canEdit}
                canDelete={gridConfig.canDelete}
                canSelect={gridConfig.canSelect}
                canSelectMultiple={false}
                canSort={true}
                isPaginated={true}
                onAdd={() => this.showResponsibilitiesDialog(null, 'add')}
                onEdit={(item) => this.showResponsibilitiesDialog(item, 'edit')}
                onDelete={(item) => this.deleteResponsibility(item)}
              />
            </MUI.Box>

            <br />

            <MUI.Box padding="8px">
              <AuthInfo
                isAdmin={false}
                isSupervisor={isSupervisor}
                isThirdLevel={isThirdLevel}
                reportHimself={(this.userInfo.IdEmpleado === this.userInfo.IdReporta)}
                permission={permission}
                minItems={minItems}
                maxItems={maxItems}
                totalItems={totalItems}
                totalSum={totalSum}
                ownItems={ownItems}
                status={status}
                onSendToAuthorize={this.sendToAuthorize}
                onReturnToActiveStatus={this.returnToActiveStatus}
                onApprove={this.approve}
                onReject={this.reject}
              />
            </MUI.Box>
          </Styled.ContentWrapper>
        </LoadingWrapper>

        {/* Modal para regresar estatus o rechazar responsabilidades */}
        {this.state.showCommentDialog.open ? (
          <CommentDialog
            action={this.state.showCommentDialog.action}
            open={this.state.showCommentDialog.open}
            onClose={() => this.closeDialog('comment')}
            onRejectConfirmation={this.rejectConfirmation}
            onReturnStatusConfirmation={this.returnStatusConfirmation}
          />
        ) : null}

        {/* Modal para añadir o editar una responsabilidad */}
        {this.state.showResponsibilitiesDialog.open ? (
          <ResponsibilitiesDialog
            action={this.state.showResponsibilitiesDialog.action}
            data={this.state.showResponsibilitiesDialog.data}
            open={this.state.showResponsibilitiesDialog.open}
            onAdd={this.addResponsibility}
            onEdit={this.editResponsibility}
            onClose={() => this.closeDialog('responsibilities')}
          />
        ) : null}
      </>
    );
  }
}

Responsibilities.propTypes = {
  // Parámetros de la URL.
  queryParams: PropTypes.object,
  // Función para cambiar al Tab de responsabilidades.
  onChangeTab: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.SearchWrapper = styled.section`
  display: flex;
  flex-wrap: wrap;
  padding: 8px;
  display: inline-block;
`;

Styled.CSVLink = styled(CSVLink)`
  text-decoration: none;
`;

Styled.ContentWrapper = styled.section`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

Styled.BoxSeeingOf = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
`;

Styled.FlexSearch = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  min-height: 72px;
`;

Styled.FlexAutocomplete = styled.div`
  flex: 1;
  .MuiFormControl-root {
    min-height: 46px;
  }
`;

Styled.Avatar = styled(Avatar)`
  margin-right: 16px;
`;

Styled.BoxIcon = styled.div`
  display: flex;
  margin-right: 16px;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

export default Responsibilities;
